import React, { useEffect, useState } from 'react';
import { hämtaKunder, hämtaMoten, hämtaInkopare, hämtaSäljare, hämtaFortnoxHändelser, Kund, Mote, Inkopare, Saljare, FortnoxHändelse } from '../api/client';
import { TEMA, useMobil } from '../theme';

function formatDatum(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Datumintervall + etikett för Fortnox-rutans egen bläddringsbara period
function beräknaFortnoxPeriod(läge: 'månad' | 'vecka', offset: number): { fran: string; till: string; label: string } {
  const nu = new Date();
  if (läge === 'månad') {
    const bas = new Date(nu.getFullYear(), nu.getMonth() - offset, 1);
    const förstaIMånaden = new Date(bas.getFullYear(), bas.getMonth(), 1);
    const sistaIMånaden = new Date(bas.getFullYear(), bas.getMonth() + 1, 0);
    const label = bas.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' });
    return { fran: formatDatum(förstaIMånaden), till: formatDatum(sistaIMånaden), label };
  } else {
    const dag = nu.getDay();
    const måndag = new Date(nu);
    måndag.setDate(nu.getDate() - (dag === 0 ? 6 : dag - 1) - offset * 7);
    måndag.setHours(0, 0, 0, 0);
    const söndag = new Date(måndag);
    söndag.setDate(måndag.getDate() + 6);
    const label = `${formatDatum(måndag)} – ${formatDatum(söndag)}`;
    return { fran: formatDatum(måndag), till: formatDatum(söndag), label };
  }
}

const FORTNOX_MAX_OFFSET_MÅNAD = 13;
const FORTNOX_MAX_OFFSET_VECKA = 56; // ungefär 13 månader i veckor

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '22px 24px',
  marginBottom: '16px',
};

const statLabelStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: TEMA.inkSoft,
  margin: '0 0 8px',
};

const btnGhostStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  padding: '9px 18px',
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: 'transparent',
  color: TEMA.inkSoft,
  cursor: 'pointer',
};

function toggleKnappStyle(aktiv: boolean): React.CSSProperties {
  return {
    padding: '7px 16px',
    borderRadius: '2px',
    fontFamily: "'Inter', sans-serif",
    fontSize: '13px',
    fontWeight: 500,
    border: `1px solid ${aktiv ? TEMA.accent : TEMA.border}`,
    cursor: 'pointer',
    background: aktiv ? TEMA.accent : 'transparent',
    color: aktiv ? TEMA.onAccent : TEMA.inkSoft,
  };
}

function pilStyle(avstängd: boolean): React.CSSProperties {
  return {
    border: `1px solid ${TEMA.border}`, background: 'transparent', borderRadius: '2px', color: TEMA.inkSoft,
    width: '28px', height: '28px', cursor: avstängd ? 'default' : 'pointer',
    opacity: avstängd ? 0.4 : 1, fontSize: '13px',
  };
}

export default function Statistik() {
  const [kunder, setKunder] = useState<Kund[]>([]);
  const [moten, setMoten] = useState<Mote[]>([]);
  const [inkopare, setInkopare] = useState<Inkopare[]>([]);
  const [säljare, setSäljare] = useState<Saljare[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);
  // En gemensam period för hela sidan: läge, och hur många perioder bakåt.
  const [läge, setLäge] = useState<'vecka' | 'månad' | 'allt'>('månad');
  const [offset, setOffset] = useState(0);
  const period = läge;

  const [utfälld, setUtfälld] = useState(false);
  const ärMobil = useMobil();
  // Fortnox kan bara visas per vecka eller månad. Vid "alla tider" hämtas inget.
  const fortnoxLäge: 'månad' | 'vecka' = läge === 'vecka' ? 'vecka' : 'månad';
  const fortnoxOffset = offset;
  const fortnoxAktiv = läge !== 'allt';
  const [fortnoxCache, setFortnoxCache] = useState<Record<string, FortnoxHändelse[]>>({});
  const [laddarHändelser, setLaddarHändelser] = useState(false);
  const [händelserFel, setHändelserFel] = useState<string | null>(null);


  useEffect(() => {
    setLaddar(true);
    setFel(null);
    Promise.all([hämtaKunder(), hämtaMoten(), hämtaInkopare(), hämtaSäljare()])
      .then(([k, m, i, s]) => {
        setKunder(k.data || []);
        setMoten(m.data);
        setInkopare(i.data);
        setSäljare(s.data || []);
        setLaddar(false);
      })
      .catch((err) => {
        console.error('Kunde inte ladda statistik:', err);
        setFel('Kunde inte ladda statistiken just nu. Försök igen om en stund.');
        setLaddar(false);
      });
  }, []);

  // Hämta ordrar/fakturor för den valda Fortnox-perioden. Sammanfattningssiffrorna
  // visas alltid (laddas direkt), medan detaljlistan bara syns när rutan fällts ut —
  // men datan hämtas oavsett, så siffrorna finns på plats direkt vid sidladdning.
  // Cachas per period så bläddring bakåt/framåt inte gör om samma nätverksanrop.
  useEffect(() => {
    if (!fortnoxAktiv) { setLaddarHändelser(false); return; }
    const cacheKey = `${fortnoxLäge}-${fortnoxOffset}`;
    if (fortnoxCache[cacheKey]) return;

    let aktuell = true;
    setLaddarHändelser(true);
    setHändelserFel(null);
    const { fran, till } = beräknaFortnoxPeriod(fortnoxLäge, fortnoxOffset);
    hämtaFortnoxHändelser(fran, till)
      .then(res => {
        if (!aktuell) return;
        setFortnoxCache(prev => ({ ...prev, [cacheKey]: res.data }));
        setLaddarHändelser(false);
      })
      .catch(err => {
        if (!aktuell) return;
        console.error('Kunde inte ladda Fortnox-händelser:', err);
        setHändelserFel('Kunde inte ladda ordrar/fakturor just nu.');
        setLaddarHändelser(false);
      });
    return () => { aktuell = false; };
  }, [fortnoxLäge, fortnoxOffset, fortnoxCache, fortnoxAktiv]);


  function filterMötenPeriod(moten: Mote[]): Mote[] {
    const besök = moten.filter(m => m.typ === 'möte' || m.typ === 'epost');
    if (läge === 'allt') return besök;
    const { fran, till } = beräknaFortnoxPeriod(läge, offset);
    return besök.filter(m => m.datum >= fran && m.datum <= till);
  }

  function exportCSV(data: object[], filnamn: string) {
    if (data.length === 0) return;
    const headers = Object.keys(data[0]);
    const rows = data.map(r => headers.map(h => `"${(r as any)[h] || ''}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filnamn;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportMöten() {
    const data = filterMötenPeriod(moten).map(m => {
      const kund = kunder.find(k => k.kund_id === m.kund_id);
      return {
        datum: m.datum,
        restaurang: kund?.namn || m.kund_id,
        stad: kund?.stad || '',
        säljare: m.säljare,
        nästa_kontakt: m.nästa_kontakt,
        notat: m.notat,
      };
    });
    exportCSV(data, `möten_${period}.csv`);
  }

  function exportKunder() {
    const data = kunder.filter(k => k.aktiv).map(k => {
      const kundInkopare = inkopare.filter(i => i.kund_id === k.kund_id);
      return {
        namn: k.namn,
        typ: k.typ,
        segment: k.segment,
        stad: k.stad,
        matkoncept: k.matkoncept,
        ansvarig_säljare: k.ansvarig_säljare,
        inköpare: kundInkopare.map(i => i.namn).join(', '),
        roller: kundInkopare.map(i => i.roll).join(', '),
      };
    });
    exportCSV(data, 'kundlista.csv');
  }

  if (laddar) return <p style={{ color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Laddar...</p>;
  if (fel) return <p style={{ color: '#D9695F', fontFamily: "'Inter', sans-serif" }}>{fel}</p>;

  const filtrerade = filterMötenPeriod(moten);

  const möttaKund = filtrerade.filter(m => {
    const k = kunder.find(k => k.kund_id === m.kund_id);
    return !!k?.fortnox_kund_nr;
  }).length;
  const möttaProspect = filtrerade.length - möttaKund;

  // Hela sidan delar en period. Fortnox-datan hämtas en gång och används av båda rutorna.
  const { fran: periodFran, till: periodTill, label: periodLabel } = läge === 'allt'
    ? { fran: '2000-01-01', till: formatDatum(new Date()), label: 'Alla tider' }
    : beräknaFortnoxPeriod(läge, offset);
  const periodMaxOffset = läge === 'månad' ? FORTNOX_MAX_OFFSET_MÅNAD : FORTNOX_MAX_OFFSET_VECKA;
  const perSäljareHändelser = fortnoxAktiv ? (fortnoxCache[`${fortnoxLäge}-${fortnoxOffset}`] || []) : [];
  const perSäljareFran = periodFran;
  const perSäljareTill = periodTill;
  const laddarPerSäljareHändelser = laddarHändelser;
  const perSäljareHändelserFel = händelserFel;

  // Fortnox "Vår referens" (var_referens) är den tillförlitliga källan för att
  // attribuera en order/faktura till rätt säljare. Den jämförs skiftlägesokänsligt
  // mot er egen säljarlista för att få rätt kapitalisering i visningen.
  // Om referensen saknas (äldre ordrar, eller inte ifylld) faller vi tillbaka på
  // att gissa via kundens ansvariga säljare. Går inget av delarna att matcha
  // hamnar händelsen under "Okänd" istället för att tappas bort tyst.
  const säljareCasingPerNamn = new Map<string, string>();
  säljare.forEach(s => { if (s.namn) säljareCasingPerNamn.set(s.namn.toLowerCase(), s.namn); });

  const säljarePerKundnamn = new Map<string, string>();
  kunder.forEach(k => {
    if (k.namn) säljarePerKundnamn.set(k.namn.toLowerCase(), k.ansvarig_säljare || '');
  });

  // Vår referens står ofta med efternamn ("Niklas Ödman") medan säljaren i
  // Vinsales heter "Niklas". Matcha på förnamnet, men bara när det är entydigt.
  const säljarePerFörnamn = new Map<string, string | null>();
  säljare.forEach(s => {
    const förnamn = (s.namn || '').trim().split(/\s+/)[0].toLowerCase();
    if (!förnamn) return;
    säljarePerFörnamn.set(förnamn, säljarePerFörnamn.has(förnamn) ? null : s.namn);
  });

  function attribueraSäljare(h: FortnoxHändelse): string {
    const referens = (h.var_referens || '').trim();
    if (referens) {
      const exakt = säljareCasingPerNamn.get(referens.toLowerCase());
      if (exakt) return exakt;
      const viaFörnamn = säljarePerFörnamn.get(referens.split(/\s+/)[0].toLowerCase());
      return viaFörnamn || referens;
    }
    const viaKund = säljarePerKundnamn.get(h.kund_namn.toLowerCase());
    return viaKund && viaKund.trim() ? viaKund : 'Okänd';
  }

  const säljareStats: Record<string, { möten: number; ordervärde: number; fakturerat: number }> = {};
  function säkraSäljareRad(namn: string) {
    if (!säljareStats[namn]) säljareStats[namn] = { möten: 0, ordervärde: 0, fakturerat: 0 };
    return säljareStats[namn];
  }

  moten
    .filter(m => (m.typ === 'möte' || m.typ === 'epost') && m.datum >= perSäljareFran && m.datum <= perSäljareTill)
    .forEach(m => { säkraSäljareRad(m.säljare).möten += 1; });

  perSäljareHändelser.forEach(h => {
    if (h.intern) return; // Internaffärer är ingen säljares försäljning.
    const rad = säkraSäljareRad(attribueraSäljare(h));
    if (h.typ === 'order') rad.ordervärde += h.total;
    else rad.fakturerat += h.total;
  });

  // Störst försäljning först, möten som andra nyckel. Okänd hamnar alltid sist.
  const perSäljareLista = Object.entries(säljareStats).sort((a, b) => {
    if (a[0] === 'Okänd') return 1;
    if (b[0] === 'Okänd') return -1;
    const totalA = a[1].ordervärde + a[1].fakturerat;
    const totalB = b[1].ordervärde + b[1].fakturerat;
    return totalB - totalA || b[1].möten - a[1].möten;
  });

  const periodLabels = { vecka: 'Vecka', månad: 'Månad', allt: 'Alla tider' };

  const formatKr = (v: number) => v.toLocaleString('sv-SE', { maximumFractionDigits: 0 }) + ' kr';

  // Fortnox-rutans egen period
  const fortnoxCacheKey = `${fortnoxLäge}-${fortnoxOffset}`;
  const aktuellaHändelser = fortnoxAktiv ? (fortnoxCache[fortnoxCacheKey] || []) : [];
  // Försäljning till interna bolag (systerbolag) räknas inte, men visas för sig.
  const externa = aktuellaHändelser.filter(h => !h.intern);
  const interna = aktuellaHändelser.filter(h => h.intern);
  const internVärde = interna.reduce((s, h) => s + h.total, 0);
  const internaBolag = Array.from(new Set(interna.map(h => h.kund_namn)));
  const antalOrdrar = externa.filter(h => h.typ === 'order').length;
  const ordervärde = externa.filter(h => h.typ === 'order').reduce((s, h) => s + h.total, 0);
  const antalFakturor = externa.filter(h => h.typ === 'faktura').length;
  const faktureratVärde = externa.filter(h => h.typ === 'faktura').reduce((s, h) => s + h.total, 0);
  const kunderSomKöpt = new Set(externa.filter(h => h.typ === 'faktura').map(h => h.kund_namn)).size;
  const antalAktiva = kunder.filter(k => k.aktiv).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px', marginBottom: '28px' }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink, margin: 0 }}>Statistik</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px 16px', flexWrap: 'wrap', justifyContent: ärMobil ? 'flex-start' : 'flex-end' }}>
          {läge !== 'allt' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => setOffset(o => Math.min(o + 1, periodMaxOffset))}
                disabled={offset >= periodMaxOffset}
                style={pilStyle(offset >= periodMaxOffset)}
              >
                ◀
              </button>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px', color: TEMA.ink, minWidth: '130px', textAlign: 'center', textTransform: 'capitalize' }}>
                {periodLabel}
              </span>
              <button
                onClick={() => setOffset(o => Math.max(o - 1, 0))}
                disabled={offset <= 0}
                style={pilStyle(offset <= 0)}
              >
                ▶
              </button>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            {(['vecka', 'månad', 'allt'] as const).map(p => (
              <button key={p} onClick={() => { setLäge(p); setOffset(0); }} style={toggleKnappStyle(läge === p)}>
                {periodLabels[p]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: ärMobil ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)', gap: ärMobil ? '10px' : '14px', marginBottom: '16px' }}>
        {([
          { label: 'Möten', värde: filtrerade.length },
          { label: 'Möten – Kund', värde: möttaKund },
          { label: 'Möten – Prospect', värde: möttaProspect },
          { label: 'Kunder som köpt', värde: !fortnoxAktiv ? '–' : laddarHändelser ? '…' : kunderSomKöpt, under: `av ${antalAktiva} aktiva` },
        ] as { label: string; värde: number | string; under?: string }[]).map(s => (
          <div key={s.label} style={{ background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '8px', padding: ärMobil ? '14px 16px' : '18px 20px', minWidth: 0 }}>
            <p style={statLabelStyle}>{s.label}</p>
            <p style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink }}>{s.värde}</p>
            {s.under && (
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>{s.under}</p>
            )}
          </div>
        ))}
      </div>

      <div style={kortStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: 0 }}>Fortnox <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: TEMA.inkFaint }}>exkl. moms</span></h2>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: TEMA.inkFaint, textTransform: 'capitalize' }}>{periodLabel}</span>
        </div>

        {laddarHändelser && <p style={{ color: TEMA.inkFaint, fontSize: '13px', marginTop: '16px', fontFamily: "'Inter', sans-serif" }}>Laddar...</p>}
        {händelserFel && <p style={{ color: '#D9695F', fontSize: '13px', marginTop: '16px', fontFamily: "'Inter', sans-serif" }}>{händelserFel}</p>}

        {!fortnoxAktiv && (
          <p style={{ color: TEMA.inkFaint, fontSize: '13px', marginTop: '16px', fontFamily: "'Inter', sans-serif" }}>
            Fortnox kan bara visas per vecka eller månad. Välj vecka eller månad ovan.
          </p>
        )}
        {fortnoxAktiv && !laddarHändelser && !händelserFel && (
          <div style={{ display: 'grid', gridTemplateColumns: ärMobil ? '1fr' : 'repeat(3, 1fr)', gap: ärMobil ? '16px' : '14px', marginTop: '20px' }}>
            {[
              { label: 'Fakturerat', värde: faktureratVärde, under: `${antalFakturor} ${antalFakturor === 1 ? 'faktura' : 'fakturor'}` },
              { label: 'Öppna ordrar', värde: ordervärde, under: `${antalOrdrar} ${antalOrdrar === 1 ? 'order' : 'ordrar'}` },
              { label: 'Totalt sålt', värde: faktureratVärde + ordervärde, under: 'fakturerat + öppna ordrar' },
            ].map((r, i) => (
              <div key={r.label} style={i === 2 ? (ärMobil ? { borderTop: `1px solid ${TEMA.border}`, paddingTop: '16px' } : { borderLeft: `1px solid ${TEMA.border}`, paddingLeft: '18px' }) : undefined}>
                <p style={statLabelStyle}>{r.label}</p>
                <p style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '20px', color: i === 2 ? TEMA.accent : TEMA.ink }}>{formatKr(r.värde)}</p>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>{r.under}</p>
              </div>
            ))}
          </div>
        )}

        {fortnoxAktiv && !laddarHändelser && !händelserFel && interna.length > 0 && (
          <p style={{ margin: '16px 0 0', fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
            Internt: <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: TEMA.inkSoft }}>{formatKr(internVärde)}</span>
            {' '}({interna.length} {interna.length === 1 ? 'post' : 'poster'} till {internaBolag.join(', ')}), ingår inte ovan.
          </p>
        )}

        {!laddarHändelser && !händelserFel && aktuellaHändelser.length > 0 && (
          <button
            onClick={() => setUtfälld(!utfälld)}
            style={{
              marginTop: '18px', height: '32px', padding: '0 14px', borderRadius: '2px',
              background: 'transparent', border: `1px solid ${TEMA.border}`, color: TEMA.inkSoft,
              fontFamily: "'Inter', sans-serif", fontSize: '13px', cursor: 'pointer',
            }}
          >
            {utfälld ? 'Dölj ordrar och fakturor' : `Visa ordrar och fakturor (${aktuellaHändelser.length})`}
          </button>
        )}

        {utfälld && (
          <>


            {!laddarHändelser && !händelserFel && (
              <div style={{ borderTop: `1px solid ${TEMA.border}`, paddingTop: '14px' }}>
                {aktuellaHändelser.length === 0 && (
                  <p style={{ color: TEMA.inkFaint, fontSize: '13px', fontFamily: "'Inter', sans-serif" }}>Inga ordrar eller fakturor under perioden.</p>
                )}
                {aktuellaHändelser.length > 0 && (
                  <>
                    {!ärMobil && <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px', padding: '0 0 10px',
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: TEMA.inkFaint, textTransform: 'uppercase',
                    }}>
                      <span style={{ width: '64px', flexShrink: 0 }}>Typ</span>
                      <span style={{ width: '80px', flexShrink: 0 }}>Ordernr</span>
                      <span style={{ width: '90px', flexShrink: 0 }}>Datum skapad</span>
                      <span style={{ flex: 1 }}>Kund</span>
                      <span>Värde</span>
                      <span style={{ width: '70px', flexShrink: 0, textAlign: 'center' }}>Status</span>
                    </div>}
                    {aktuellaHändelser.map((h, idx) => (
                      <div key={`${h.typ}-${h.dokument_nr}-${idx}`} title={h.intern ? 'Internt bolag, ingår inte i försäljningen' : undefined} style={{
                        opacity: h.intern ? 0.55 : 1,
                        display: 'flex', alignItems: 'center', gap: ärMobil ? '6px 10px' : '10px', padding: '10px 0',
                        flexWrap: ärMobil ? 'wrap' : undefined,
                        borderBottom: idx < aktuellaHändelser.length - 1 ? `1px solid ${TEMA.border}` : 'none',
                      }}>
                        <span style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: '11px', padding: '2px 8px', borderRadius: '2px',
                          border: `1px solid ${h.typ === 'faktura' ? TEMA.accent : TEMA.border}`,
                          color: h.typ === 'faktura' ? TEMA.accent : TEMA.inkSoft,
                          width: '64px', flexShrink: 0, textAlign: 'center', boxSizing: 'border-box',
                        }}>
                          {h.typ === 'faktura' ? 'Faktura' : 'Order'}
                        </span>
                        <span style={{ fontSize: '13px', color: TEMA.inkFaint, width: '80px', flexShrink: 0, fontFamily: "'IBM Plex Mono', monospace" }}>#{h.dokument_nr}</span>
                        <span style={{ fontSize: '13px', color: TEMA.inkFaint, width: ärMobil ? 'auto' : '90px', flexShrink: 0, fontFamily: "'IBM Plex Mono', monospace" }}>{h.datum}</span>
                        {/* Radbrytning på mobil: kund, belopp och status på egen rad */}
                        {ärMobil && <span aria-hidden="true" style={{ flexBasis: '100%', height: 0 }} />}
                        <span style={{ fontSize: '14px', flex: 1, minWidth: 0, color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>
                          {h.kund_namn}
                          {h.intern && (
                            <span style={{
                              marginLeft: '8px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '10.5px',
                              padding: '1px 6px', borderRadius: '2px', border: `1px solid ${TEMA.border}`, color: TEMA.inkSoft,
                            }}>Internt</span>
                          )}
                        </span>
                        <span style={{ fontSize: '14px', color: TEMA.ink, fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap' }}>{formatKr(h.total)}</span>
                        {h.typ === 'faktura' ? (
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: '11px', padding: '2px 8px', borderRadius: '2px', width: '70px', textAlign: 'center', flexShrink: 0,
                            border: `1px solid ${h.betald ? TEMA.positive : TEMA.border}`,
                            color: h.betald ? TEMA.positive : TEMA.inkFaint, boxSizing: 'border-box',
                          }}>
                            {h.betald ? 'Betald' : 'Obetald'}
                          </span>
                        ) : (
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: '11px', padding: '2px 8px', borderRadius: '2px', width: '70px', textAlign: 'center', flexShrink: 0,
                            border: `1px solid ${TEMA.border}`, color: TEMA.inkSoft, boxSizing: 'border-box',
                          }}>
                            Öppen
                          </span>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div style={kortStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: 0 }}>Per säljare <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: TEMA.inkFaint }}>exkl. moms</span></h2>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: TEMA.inkFaint, textTransform: 'capitalize' }}>{periodLabel}</span>
        </div>

        {perSäljareHändelserFel && <p style={{ color: '#D9695F', fontSize: '13px', marginBottom: '14px', fontFamily: "'Inter', sans-serif" }}>{perSäljareHändelserFel}</p>}

        {perSäljareLista.length === 0 ? (
          <p style={{ color: TEMA.inkFaint, fontSize: '13px', fontFamily: "'Inter', sans-serif" }}>Inga möten eller Fortnox-händelser under perioden.</p>
        ) : (
          <>
            {!ärMobil && <div style={{
              display: 'flex', alignItems: 'center', gap: '14px', padding: '0 0 10px',
              fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: TEMA.inkFaint, textTransform: 'uppercase',
            }}>
              <span style={{ flex: 1 }}>Säljare</span>
              <span style={{ width: '70px', textAlign: 'right', flexShrink: 0 }}>Möten</span>
              <span style={{ width: '110px', textAlign: 'right', flexShrink: 0 }}>
                Öppna ordrar{laddarPerSäljareHändelser ? ' …' : ''}
              </span>
              <span style={{ width: '110px', textAlign: 'right', flexShrink: 0 }}>
                Fakturerat{laddarPerSäljareHändelser ? ' …' : ''}
              </span>
              <span style={{ width: '120px', textAlign: 'right', flexShrink: 0 }}>
                Totalt sålt
              </span>
            </div>}
            {perSäljareLista.map(([säljare, rad], idx) => ärMobil ? (
              <div key={säljare} style={{
                padding: '12px 0',
                borderBottom: idx < perSäljareLista.length - 1 ? `1px solid ${TEMA.border}` : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: säljare === 'Okänd' ? 'transparent' : 'rgba(224,168,62,0.12)',
                    border: säljare === 'Okänd' ? `1px solid ${TEMA.border}` : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', fontWeight: 500,
                    color: säljare === 'Okänd' ? TEMA.inkFaint : TEMA.accent, flexShrink: 0,
                  }}>
                    {säljare.charAt(0).toUpperCase()}
                  </div>
                  <span style={{ fontSize: '14px', color: säljare === 'Okänd' ? TEMA.inkFaint : TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{säljare}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '14px', color: TEMA.accent, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {fortnoxAktiv ? formatKr(rad.ordervärde + rad.fakturerat) : '–'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', paddingLeft: '38px' }}>
                  {[
                    ['Möten', String(rad.möten)],
                    ['Öppna ordrar', fortnoxAktiv ? formatKr(rad.ordervärde) : '–'],
                    ['Fakturerat', fortnoxAktiv ? formatKr(rad.fakturerat) : '–'],
                  ].map(([etikett, värde]) => (
                    <div key={etikett} style={{ minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: '11px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>{etikett}</p>
                      <p style={{ margin: '2px 0 0', fontSize: '12.5px', color: TEMA.ink, fontFamily: "'IBM Plex Mono', monospace" }}>{värde}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div key={säljare} style={{
                display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 0',
                borderBottom: idx < perSäljareLista.length - 1 ? `1px solid ${TEMA.border}` : 'none',
              }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%',
                    background: säljare === 'Okänd' ? 'transparent' : 'rgba(224,168,62,0.12)',
                    border: säljare === 'Okänd' ? `1px solid ${TEMA.border}` : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', fontWeight: 500,
                    color: säljare === 'Okänd' ? TEMA.inkFaint : TEMA.accent, flexShrink: 0,
                  }}>
                    {säljare.charAt(0).toUpperCase()}
                  </div>
                  <span style={{ fontSize: '14px', color: säljare === 'Okänd' ? TEMA.inkFaint : TEMA.ink, fontFamily: "'Inter', sans-serif" }}>{säljare}</span>
                </div>
                <span style={{ width: '70px', textAlign: 'right', flexShrink: 0, fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'IBM Plex Mono', monospace" }}>{rad.möten}</span>
                <span style={{ width: '110px', textAlign: 'right', flexShrink: 0, fontSize: '13px', color: TEMA.ink, fontFamily: "'IBM Plex Mono', monospace" }}>{fortnoxAktiv ? formatKr(rad.ordervärde) : '–'}</span>
                <span style={{ width: '110px', textAlign: 'right', flexShrink: 0, fontSize: '13px', color: TEMA.ink, fontFamily: "'IBM Plex Mono', monospace" }}>{fortnoxAktiv ? formatKr(rad.fakturerat) : '–'}</span>
                <span style={{ width: '120px', textAlign: 'right', flexShrink: 0, fontSize: '13px', color: TEMA.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{fortnoxAktiv ? formatKr(rad.ordervärde + rad.fakturerat) : '–'}</span>
              </div>
            ))}
            {fortnoxAktiv && perSäljareLista.some(([namn]) => namn === 'Okänd') && (
              <p style={{ margin: '14px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
                Okänd: ordrar och fakturor där Vår referens saknas i Fortnox och kunden inte har någon ansvarig säljare i Vinsales. Fyll i Vår referens på ordern, eller ansvarig säljare på kunden, så hamnar de rätt.
              </p>
            )}
          </>
        )}
      </div>

      <div style={kortStyle}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: '0 0 18px' }}>Exportera data</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          <button onClick={exportMöten} style={btnGhostStyle}>
            Möten ({periodLabel}) → CSV
          </button>
          <button onClick={exportKunder} style={btnGhostStyle}>
            Kundlista → CSV
          </button>
        </div>
      </div>
    </div>
  );
}